import React, { useState } from "react";
import PropTypes from 'prop-types';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import db from '../firebase';
import AppDescription from "./AppDescription";
import { validateEmail, validatePassword, getFirebaseErrorMessage } from '../utils/validation';

const userActions = {
  login: {
    title: "Login",
    topBtnTxt: "Create Account",
    submitBtnTxt: "Login",
    action: signInWithEmailAndPassword,
    autoComplete: "current-password"
  },
  createAccount: {
    title: "Create An Account",
    topBtnTxt: "Already a user, go to login",
    submitBtnTxt: "Generate account",
    action: createUserWithEmailAndPassword,
    autoComplete: "new-password"
  }
};

function HandleUser() {
  const [inputEmail, setInputEmail] = useState("");
  const [inputPswrd, setInputPswrd] = useState("");
  const [apiMessage, setApiMessage] = useState("");
  const [currentAction, setCurrentAction] = useState("login");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const auth = getAuth();

  const toggleUserAction = () => {
    setCurrentAction(currentAction === "login" ? "createAccount" : "login");
    setApiMessage("");
    setEmailError("");
    setPasswordError("");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setApiMessage("");
    setEmailError("");
    setPasswordError("");

    // Validate email
    const emailValidation = validateEmail(inputEmail);
    if (!emailValidation.valid) {
      setEmailError(emailValidation.error);
      return;
    }

    // Validate password
    const passwordValidation = validatePassword(inputPswrd);
    if (!passwordValidation.valid) {
      setPasswordError(passwordValidation.error);
      return;
    }

    // Start loading
    setIsLoading(true);
    const action = userActions[currentAction].action;

    try {
      const userCredential = await action(auth, inputEmail, inputPswrd);
      if (currentAction === "createAccount") {
        const user = userCredential.user;
        await setDoc(doc(db, "users", user.uid), { userId: user.uid, email: user.email });
      }
    } catch (error) {
      console.error(`${currentAction} error:`, error);
      const errorMessage = getFirebaseErrorMessage(error.code) || "An error occurred. Please try again.";
      setApiMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = setter => e => setter(e.target.value);

  return (
    <div className="handleUserCard">
      <button className="redBtn mainBtn" onClick={toggleUserAction} disabled={isLoading}>
        {userActions[currentAction].topBtnTxt}
      </button>
      <h1>{userActions[currentAction].title}</h1>
      <form onSubmit={handleFormSubmit} id="userForm" autoComplete="on">
        <FormField
          id="email"
          label="Email"
          type="email"
          value={inputEmail}
          autoComplete="username"
          onChange={(e) => {
            updateField(setInputEmail)(e);
            if (emailError) setEmailError("");
          }}
          error={emailError}
          disabled={isLoading}
        />
        <FormField
          id="password"
          label="Password"
          type="password"
          value={inputPswrd}
          autoComplete={userActions[currentAction].autoComplete}
          onChange={(e) => {
            updateField(setInputPswrd)(e);
            if (passwordError) setPasswordError("");
          }}
          error={passwordError}
          disabled={isLoading}
        />
        {apiMessage && <div style={{ color: "#f44336", fontSize: "14px", marginBottom: "8px" }}>{apiMessage}</div>}
        <button type="submit" className="mainBtn greenBtn" disabled={isLoading}>
          {isLoading ? 'Please wait...' : userActions[currentAction].submitBtnTxt}
        </button>
      </form>
      <AppDescription />
    </div>
  );
}

function FormField({ id, label, type, value, autoComplete, onChange, error, disabled }) {
  return (
    <div>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        aria-required="true"
        autoComplete={autoComplete}
        onChange={onChange}
        disabled={disabled}
      />
      {error && <div style={{ color: "#f44336", fontSize: "12px", marginTop: "4px" }}>{error}</div>}
    </div>
  );
}

FormField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  autoComplete: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool,
};

export default HandleUser;
